import { useEffect, useState } from 'react'
import { Loader2, MessageSquare } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { CompanyPhoneNumber } from '@/api/company'
import { useSubmitTollfreeSms, useTollfreeSms, type TollfreeSmsForm, type TollfreeSmsStatus } from '@/api/phoneNumbers'

/**
 * Textos depuis un sans-frais : les opérateurs exigent une vérification de l'entreprise.
 * Ici la pastille d'état sur le numéro et la fenêtre du dossier, en libre-service.
 */

export function tollfreeSmsTone(status: TollfreeSmsStatus | undefined) {
  if (status === 'approved') return 'slate'
  if (status === 'pending') return 'blue'
  if (status === 'rejected') return 'red'
  return 'amber'
}

export function TollfreeSmsPill({ number, className }: { number: CompanyPhoneNumber; className?: string }) {
  const { t } = useTranslation()
  if (!number.is_toll_free || number.tollfree_sms_status === 'approved') {
    return <span className={cn('inline-flex items-center px-2 py-1 rounded bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 text-xs border border-slate-200 dark:border-slate-700', className)}>SMS</span>
  }
  const s = number.tollfree_sms_status || 'unknown'
  const tone = tollfreeSmsTone(s)
  return (
    <span className={cn('inline-flex items-center px-2 py-1 rounded text-xs border',
      tone === 'blue' && 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-500/30',
      tone === 'red' && 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/30',
      tone === 'amber' && 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30',
      className)}>
      {t(`manager.numbers.tollfreeSms.pill.${s === 'unknown' ? 'none' : s}`)}
    </span>
  )
}

/** Bloc dans la fiche du numéro : l'état en mots simples et le bouton pour déposer le dossier. */
export function TollfreeSmsSection({ number, companyId }: { number: CompanyPhoneNumber; companyId: number | undefined }) {
  const { t } = useTranslation()
  const etat = useTollfreeSms(companyId, number.id, number.is_toll_free)
  const [ouvert, setOuvert] = useState(false)
  if (!number.is_toll_free) return null
  const s: TollfreeSmsStatus = etat.data?.status ?? number.tollfree_sms_status ?? 'unknown'
  const cle = s === 'unknown' ? 'none' : s
  return (
    <section>
      <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-slate-400" />
        {t('manager.numbers.tollfreeSms.title')}
      </h2>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{t('manager.numbers.tollfreeSms.intro')}</p>
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-sm text-slate-900 dark:text-white">{t(`manager.numbers.tollfreeSms.state.${cle}.title`)}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t(`manager.numbers.tollfreeSms.state.${cle}.text`)}</p>
          {s === 'rejected' && etat.data?.reason && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">{etat.data.reason}</p>
          )}
        </div>
        {(s === 'none' || s === 'unknown' || s === 'rejected') && (
          <Button type="button" onClick={() => setOuvert(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
            {t('manager.numbers.tollfreeSms.activate')}
          </Button>
        )}
      </div>
      <TollfreeSmsDialog open={ouvert} onClose={() => setOuvert(false)} number={number} companyId={companyId} prefill={etat.data?.prefill} />
    </section>
  )
}

const VIDE: TollfreeSmsForm = {
  business_name: '', website: '', address1: '', address2: '', city: '', province: '', postal: '', country: 'CA',
  contact_first_name: '', contact_last_name: '', contact_email: '', contact_phone: '',
  use_case: 'service_client', use_case_summary: '', sample_message: '', message_volume: '1,000',
}
const CAS = ['service_client', 'rendez_vous', 'livraisons', 'marketing', 'mixte']
const VOLUMES = ['10', '100', '1,000', '10,000', '100,000']

export function TollfreeSmsDialog({ open, onClose, number, companyId, prefill }: {
  open: boolean; onClose: () => void; number: CompanyPhoneNumber; companyId: number | undefined; prefill?: TollfreeSmsForm
}) {
  const { t } = useTranslation()
  const [f, setF] = useState<TollfreeSmsForm>(VIDE)
  const soumettre = useSubmitTollfreeSms(companyId, number.id)

  useEffect(() => {
    if (open) setF({ ...VIDE, ...(prefill ?? {}), use_case: 'service_client', message_volume: '1,000' })
  }, [open, prefill])

  const set = (k: keyof TollfreeSmsForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF((x) => ({ ...x, [k]: e.target.value }))
  const requis: (keyof TollfreeSmsForm)[] = ['business_name', 'address1', 'city', 'province', 'postal', 'contact_first_name', 'contact_last_name', 'contact_email', 'contact_phone', 'use_case_summary', 'sample_message']
  // Some carriers require the business website for this check (the server says which).
  if (number.fournisseur === 'telnyx') requis.push('website')
  const complet = requis.every((k) => (f[k] || '').trim())

  const envoyer = async () => {
    try {
      await soumettre.mutateAsync(f)
      toast.success(t('manager.numbers.tollfreeSms.submitted'))
      onClose()
    } catch (e) {
      const d = isAxiosError(e) ? (e.response?.data as { error?: string; detail?: string } | undefined) : undefined
      toast.error(t('manager.numbers.tollfreeSms.submitError'), { description: d?.detail })
    }
  }

  const champ = (k: keyof TollfreeSmsForm, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <div className="space-y-1">
      <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">{label}</Label>
      <Input value={f[k] || ''} onChange={set(k)} {...props} />
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent showCloseButton={false} className="sm:max-w-2xl overflow-hidden border-0 p-0 shadow-2xl">
        <div className="h-1 w-full bg-gradient-to-r from-blue-600 via-blue-400 to-blue-600" />
        <div className="px-6 pt-6">
          <DialogTitle className="text-xl tracking-tight">{t('manager.numbers.tollfreeSms.dialog.title')}</DialogTitle>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t('manager.numbers.tollfreeSms.dialog.subtitle')}</p>
        </div>

        <div className="px-6 pt-5 pb-2 space-y-5 max-h-[65vh] overflow-y-auto">
          <div>
            <p className="text-sm font-semibold mb-2">{t('manager.numbers.tollfreeSms.dialog.business')}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {champ('business_name', t('manager.numbers.tollfreeSms.dialog.businessName'))}
              {champ('business_number', t('manager.numbers.tollfreeSms.dialog.businessNumber'), { placeholder: 'NEQ 1234567890 ou BN 123456789RC0001' })}
              {champ('website', t('manager.numbers.tollfreeSms.dialog.website'), { placeholder: 'https://…' })}
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('manager.numbers.tollfreeSms.dialog.businessEmail')}</Label>
                <Input type="email" value={f.business_email || ''} onChange={set('business_email')} placeholder="info@votre-entreprise.ca" />
                <p className="text-[11px] text-slate-400">{t('manager.numbers.tollfreeSms.dialog.businessEmailHelp')}</p>
              </div>
              <div className="sm:col-span-2">{champ('address1', t('manager.numbers.tollfreeSms.dialog.address'))}</div>
              {champ('city', t('manager.numbers.tollfreeSms.dialog.city'))}
              <div className="grid grid-cols-2 gap-3">
                {champ('province', t('manager.numbers.tollfreeSms.dialog.province'), { placeholder: 'QC' })}
                {champ('postal', t('manager.numbers.tollfreeSms.dialog.postal'), { placeholder: 'H2X 1Y4' })}
              </div>
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold mb-2">{t('manager.numbers.tollfreeSms.dialog.contact')}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {champ('contact_first_name', t('manager.numbers.tollfreeSms.dialog.firstName'))}
              {champ('contact_last_name', t('manager.numbers.tollfreeSms.dialog.lastName'))}
              {champ('contact_email', t('manager.numbers.tollfreeSms.dialog.email'), { type: 'email' })}
              {champ('contact_phone', t('manager.numbers.tollfreeSms.dialog.phone'), { type: 'tel' })}
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold mb-2">{t('manager.numbers.tollfreeSms.dialog.usage')}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('manager.numbers.tollfreeSms.dialog.useCase')}</Label>
                <Select value={f.use_case} onValueChange={(v) => setF((x) => ({ ...x, use_case: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CAS.map((c) => <SelectItem key={c} value={c}>{t(`manager.numbers.tollfreeSms.useCases.${c}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('manager.numbers.tollfreeSms.dialog.volume')}</Label>
                <Select value={f.message_volume} onValueChange={(v) => setF((x) => ({ ...x, message_volume: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VOLUMES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1 mt-3">
              <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('manager.numbers.tollfreeSms.dialog.summary')}</Label>
              <textarea rows={2} value={f.use_case_summary || ''} onChange={set('use_case_summary')} placeholder={t('manager.numbers.tollfreeSms.dialog.summaryPlaceholder')}
                className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="space-y-1 mt-3">
              <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('manager.numbers.tollfreeSms.dialog.sample')}</Label>
              <textarea rows={2} value={f.sample_message || ''} onChange={set('sample_message')} placeholder={t('manager.numbers.tollfreeSms.dialog.samplePlaceholder')}
                className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <p className="text-xs text-slate-500 dark:text-slate-400">{t('manager.numbers.tollfreeSms.dialog.note')}</p>
        </div>

        <div className="flex justify-end gap-2 border-t bg-slate-50 dark:bg-slate-800/50 px-6 py-4">
          <Button type="button" variant="ghost" onClick={onClose} disabled={soumettre.isPending}>{t('manager.calendars.modal.cancel')}</Button>
          <Button type="button" onClick={() => void envoyer()} disabled={!complet || soumettre.isPending} className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
            {soumettre.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('manager.numbers.tollfreeSms.dialog.submit')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
